import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { DollarSign, TrendingUp } from 'lucide-react';

export const ROICalculator = ({ onOpenQuiz }) => {
  const [missedCalls, setMissedCalls] = useState(5);
  const [avgJobValue, setAvgJobValue] = useState(1200);

  const weeklyRevenue = missedCalls * avgJobValue;
  const annualRevenue = weeklyRevenue * 52;

  return (
    <section id="roi-calculator" className="py-24 sm:py-32 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl font-headline font-bold text-[#F4F4F5] mb-4">
            What is <span className="gradient-text">"Voicemail"</span> costing you?
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="glass p-5 sm:p-12 rounded-2xl space-y-8"
        >
          {/* Missed Calls Slider */}
          <div>
            <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <span className="text-base sm:text-lg font-body font-semibold text-[#F4F4F5]">
                How many calls do you miss per week?
              </span>
              <span className="text-3xl font-headline font-bold gradient-text" data-testid="roi-missed-calls-value">
                {missedCalls}
              </span>
            </label>
            <input
              type="range"
              min="1"
              max="20"
              value={missedCalls}
              onChange={(e) => setMissedCalls(Number(e.target.value))}
              className="w-full h-3 bg-[#1C1C1E] rounded-lg appearance-none cursor-pointer slider-thumb"
              style={{
                background: `linear-gradient(to right, #00E599 0%, #00E599 ${(missedCalls / 20) * 100}%, #1C1C1E ${(missedCalls / 20) * 100}%, #1C1C1E 100%)`
              }}
            />
          </div>

          {/* Average Job Value Input */}
          <div>
            <label className="block text-lg font-body font-semibold text-[#F4F4F5] mb-4">
              What is your average job value?
            </label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[#00E599]" />
              <input
                type="number"
                value={avgJobValue}
                onChange={(e) => setAvgJobValue(Number(e.target.value))}
                className="w-full pl-14 pr-4 py-4 bg-[#1C1C1E] border border-white/10 rounded-lg text-[#F4F4F5] text-2xl font-headline font-bold focus:outline-none focus:border-[#00E599] transition-colors"
              />
            </div>
          </div>

          {/* Results */}
          <div className="glass p-5 sm:p-8 rounded-xl border-2 border-[#00E599] bg-[#00E599]/5">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-7 h-7 sm:w-8 sm:h-8 text-[#00E599] shrink-0" />
              <h3 className="text-base sm:text-xl font-headline font-bold text-[#F4F4F5]">Your Potential Recovered Revenue:</h3>
            </div>
            <div className="space-y-2">
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-4xl sm:text-6xl font-headline font-bold gradient-text break-all">
                  ${annualRevenue.toLocaleString()}
                </span>
                <span className="text-xl sm:text-2xl text-[#A1A1AA]">/ year</span>
              </div>
              <p className="text-base sm:text-lg text-[#A1A1AA] font-body">
                (${weeklyRevenue.toLocaleString()} per week)
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center pt-4">
            <Button
              onClick={onOpenQuiz}
              size="lg"
              className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-10 py-5 sm:py-6 text-sm sm:text-xl font-body font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-[#00E599]/40 rounded-xl"
            >
              See how many jobs you're missing.
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ROICalculator;
